import { unzipSync, zipSync } from 'fflate';
import type { Class, EvalSample, NewProjectInput, Project, ProjectKind, Sample } from './types';

/**
 * The `.bryta` project file: a single zip containing project metadata,
 * classes, sample blobs (images for image projects, audio for audio), and
 * eval samples. Trained model weights are intentionally NOT included in v1
 * — when a kid opens a project on a new device, they re-train. This keeps
 * the format small and forces "your model is honest about the data it just
 * saw" semantics. The kid sees their pictures restored, hits Train.
 *
 * Schema versioning is explicit (`brytaSchemaVersion`) so we can evolve
 * the format without silently breaking older files. Importers reject any
 * version they don't recognize.
 */

export const BRYTA_FILE_SCHEMA_VERSION = 1;

interface ManifestSampleV1 {
  id: string;
  classId: string;
  createdAt: number;
  /** Filename inside the zip — e.g. `samples/{id}.png`. */
  path: string;
  mimeType: string;
}

interface ManifestEvalSampleV1 extends ManifestSampleV1 {
  /** Eval samples carry their own truth class. */
  truthClassId: string;
}

interface ManifestClassV1 {
  id: string;
  name: string;
  position: number;
}

interface ManifestProjectV1 {
  id: string;
  name: string;
  kind: ProjectKind;
}

interface ManifestV1 {
  brytaSchemaVersion: 1;
  exportedAt: string;
  project: ManifestProjectV1;
  classes: ManifestClassV1[];
  samples: ManifestSampleV1[];
  evalSamples: ManifestEvalSampleV1[];
}

export interface ProjectExportInput {
  project: Project;
  classes: Class[];
  /** All training samples for the project, any order. */
  samples: Sample[];
  /** All held-out eval samples for the project. */
  evalSamples: EvalSample[];
}

export interface ProjectImport {
  /** Just the bits needed to feed `storage.create()`. The opener picks the
   * new project ID — old IDs are not reused. */
  projectInput: NewProjectInput;
  /** Class definitions in their original order. The importer creates these
   * in order and gets new IDs from storage. */
  classes: Array<{ originalId: string; name: string; position: number }>;
  /** Samples grouped by their *original* class ID. Importer maps these
   * onto the new class IDs after creating the classes. */
  samplesByOriginalClassId: Record<string, Array<{ blob: Blob; createdAt: number }>>;
  /** Eval samples grouped by their *original* truth class ID. */
  evalSamplesByOriginalClassId: Record<string, Array<{ blob: Blob; createdAt: number }>>;
}

/**
 * Pack a project into a `.bryta` file. The returned Blob can be saved to
 * disk via the standard `<a download>` dance. Pure: no DOM, no DB.
 */
export async function packProjectFile(input: ProjectExportInput): Promise<Blob> {
  const encode = (s: string): Uint8Array => new Uint8Array(new TextEncoder().encode(s));
  const files: Record<string, Uint8Array> = {};

  const sampleManifest: ManifestSampleV1[] = [];
  for (const s of input.samples) {
    const ext = guessExtension(s.blob.type);
    const path = `samples/${s.id}${ext}`;
    const buf = await s.blob.arrayBuffer();
    files[path] = new Uint8Array(buf);
    sampleManifest.push({
      id: s.id,
      classId: s.classId,
      createdAt: s.createdAt,
      path,
      mimeType: s.blob.type || 'application/octet-stream',
    });
  }

  const evalManifest: ManifestEvalSampleV1[] = [];
  for (const s of input.evalSamples) {
    const ext = guessExtension(s.blob.type);
    const path = `eval/${s.id}${ext}`;
    const buf = await s.blob.arrayBuffer();
    files[path] = new Uint8Array(buf);
    evalManifest.push({
      id: s.id,
      classId: s.classId,
      truthClassId: s.classId,
      createdAt: s.createdAt,
      path,
      mimeType: s.blob.type || 'application/octet-stream',
    });
  }

  const manifest: ManifestV1 = {
    brytaSchemaVersion: BRYTA_FILE_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    project: { id: input.project.id, name: input.project.name, kind: input.project.kind },
    classes: input.classes.map((c) => ({ id: c.id, name: c.name, position: c.position })),
    samples: sampleManifest,
    evalSamples: evalManifest,
  };
  files['manifest.json'] = encode(JSON.stringify(manifest, null, 2));

  const zipped = zipSync(files);
  // Wrap in a fresh ArrayBuffer-backed Uint8Array so the Blob constructor
  // accepts it under TS strict (lib.dom expects ArrayBufferView<ArrayBuffer>).
  const copy = new Uint8Array(zipped.byteLength);
  copy.set(zipped);
  return new Blob([copy], { type: 'application/zip' });
}

/** Sentinel error so the UI can show a kid-friendly message instead of leaking
 *  zip-corruption noise. */
export class ProjectFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectFileError';
  }
}

/**
 * Read a `.bryta` file and decode it into something the importer can feed
 * to storage. Accepts Blob (production path: a `File` from a file input) or
 * raw bytes (test path; jsdom's Blob.arrayBuffer() does not preserve binary
 * payloads reliably enough for unit tests). Pure: no DOM, no DB. Throws
 * ProjectFileError when the file is missing the manifest, has an unsupported
 * schema, or is malformed.
 */
export async function unpackProjectFile(
  file: Blob | ArrayBuffer | Uint8Array,
): Promise<ProjectImport> {
  let entries: Record<string, Uint8Array>;
  try {
    let bytes: Uint8Array;
    if (file instanceof Uint8Array) {
      bytes = file;
    } else if (file instanceof ArrayBuffer) {
      bytes = new Uint8Array(file);
    } else {
      bytes = new Uint8Array(await file.arrayBuffer());
    }
    entries = unzipSync(bytes);
  } catch {
    throw new ProjectFileError("That doesn't look like a Bryta project file.");
  }

  const manifestEntry = entries['manifest.json'];
  if (!manifestEntry) {
    throw new ProjectFileError('Project file is missing its manifest.');
  }

  let manifest: ManifestV1;
  try {
    manifest = JSON.parse(new TextDecoder().decode(manifestEntry)) as ManifestV1;
  } catch {
    throw new ProjectFileError('Project file manifest is unreadable.');
  }

  if (manifest.brytaSchemaVersion !== BRYTA_FILE_SCHEMA_VERSION) {
    throw new ProjectFileError(
      `This project file is version ${manifest.brytaSchemaVersion}, which this Bryta cannot open.`,
    );
  }
  if (!manifest.project || !manifest.classes) {
    throw new ProjectFileError('Project file manifest is incomplete.');
  }

  const samplesByOriginalClassId: Record<string, Array<{ blob: Blob; createdAt: number }>> = {};
  for (const s of manifest.samples ?? []) {
    const data = entries[s.path];
    if (!data) continue;
    const blob = new Blob([data.slice().buffer], { type: s.mimeType });
    const bucket = samplesByOriginalClassId[s.classId] ?? [];
    bucket.push({ blob, createdAt: s.createdAt });
    samplesByOriginalClassId[s.classId] = bucket;
  }

  const evalSamplesByOriginalClassId: Record<string, Array<{ blob: Blob; createdAt: number }>> = {};
  for (const s of manifest.evalSamples ?? []) {
    const data = entries[s.path];
    if (!data) continue;
    const blob = new Blob([data.slice().buffer], { type: s.mimeType });
    const bucket = evalSamplesByOriginalClassId[s.truthClassId] ?? [];
    bucket.push({ blob, createdAt: s.createdAt });
    evalSamplesByOriginalClassId[s.truthClassId] = bucket;
  }

  return {
    projectInput: { name: manifest.project.name, kind: manifest.project.kind },
    classes: [...manifest.classes]
      .sort((a, b) => a.position - b.position)
      .map((c) => ({ originalId: c.id, name: c.name, position: c.position })),
    samplesByOriginalClassId,
    evalSamplesByOriginalClassId,
  };
}

/** Build a kid-friendly filename stem for a saved project file. Mirrors
 *  trainer.ts's `buildExportFilename` but uses a `.bryta` extension. */
export function buildProjectFileName(projectName: string): string {
  const trimmed = projectName.trim().toLowerCase();
  const slug = trimmed.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug ? `${slug}.bryta` : 'project.bryta';
}

function guessExtension(mimeType: string): string {
  if (!mimeType) return '';
  if (mimeType === 'image/png') return '.png';
  if (mimeType === 'image/jpeg') return '.jpg';
  if (mimeType === 'image/webp') return '.webp';
  if (mimeType === 'audio/wav' || mimeType === 'audio/x-wav') return '.wav';
  if (mimeType === 'audio/webm') return '.webm';
  if (mimeType === 'audio/mpeg') return '.mp3';
  return '';
}
